import React from 'react';
import { FiAward, FiExternalLink, FiShield, FiFileText } from 'react-icons/fi';
import buildResolvedFile from '../utils/buildResolvedFile';
import useBrandColors from '../utils/useBrandColors';

// Helper function to resolve the logo or certificate source safely
const getResolvedUrl = (path, apiBaseURL) => {
  if (!path) return '';
  return buildResolvedFile(path, apiBaseURL).resolvedSrc || '';
};

// Accreditation Card Component
const AccreditationCard = ({ item, apiBaseURL }) => {
  const logoSrc = getResolvedUrl(item.logo || item.image || item.imageUrl, apiBaseURL);
  const certificateSrc = getResolvedUrl(item.certificate, apiBaseURL);
  const { primary, secondary, fontBody, fontHeading } = useBrandColors();

  return (
    <div 
      className="group relative bg-white rounded-2xl overflow-hidden transition-all duration-500 hover:shadow-xl border flex flex-col justify-between p-6 w-[340px] min-h-[420px] max-h-[600px] flex-shrink-0"
      style={{ borderColor: `${primary}15` }}
    >
      <div className="flex flex-col flex-grow text-center overflow-y-auto custom-scrollbar" style={{ fontFamily: fontBody }}>
        {/* Logo */}
        <div className="flex items-center justify-center h-20 mb-4 flex-shrink-0">
          {logoSrc ? (
            <img 
              src={logoSrc} 
              alt={item.name}
              className="h-16 w-auto object-contain transition-transform duration-500 group-hover:scale-105"
              loading="lazy"
            />
          ) : (
            <div 
              className="w-16 h-16 rounded-2xl flex items-center justify-center mx-auto"
              style={{ 
                background: `linear-gradient(135deg, ${primary}15, ${secondary}15)`,
              }}
            >
              <FiAward className="w-8 h-8" style={{ color: secondary }} />
            </div>
          )}
        </div>

        {/* Name */}
        <h4 
          className="text-lg font-bold mb-1"
          style={{ 
            color: '#1F2937',
            fontFamily: fontHeading,
          }}
        >
          {item.name}
        </h4>
        
        {item.fullName && (
          <p className="text-sm mb-3 text-gray-500">
            {item.fullName}
          </p>
        )}

        {/* Grade Badge */}
        <div className="h-8 mb-3 flex items-center justify-center flex-shrink-0">
          {item.grade ? (
            <span 
              className="inline-block px-4 py-1 rounded-full text-sm font-medium"
              style={{ 
                backgroundColor: `${secondary}15`,
                color: secondary,
                borderColor: `${secondary}30`,
              }}
            >
              Grade: {item.grade}
            </span>
          ) : null}
        </div>

        {/* Full Content / Description */}
        {item.description && (
          <p className="text-sm leading-relaxed mb-6 text-gray-600 text-left px-2">
            {item.description}
          </p>
        )}
      </div>

      {/* Action Links & Document Handler */}
      <div className="flex flex-col gap-3 pt-3 mt-auto border-t border-gray-100 flex-shrink-0 bg-white">
        {/* Unified Certificate Option */}
        {certificateSrc && (
          <div className="w-full">
            <a
              href={certificateSrc}
              target="_blank"
              rel="noopener noreferrer"
              className="w-full inline-flex items-center justify-center gap-2 px-4 py-2 text-xs font-medium rounded-xl border transition-all duration-300 hover:scale-[1.01]"
              style={{ 
                color: primary, 
                borderColor: `${primary}30`,
                backgroundColor: `${primary}05`
              }}
            >
              <FiFileText className="w-4 h-4 text-gray-500" />
              <span>View Certificate</span>
            </a>
          </div>
        )}

        {/* External URL Link */}
        {item.link && (
          <div className="text-center">
            <a
              href={item.link}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 text-sm transition-colors group/link font-medium"
              style={{ color: secondary }}
            >
              <span>Learn More</span>
              <FiExternalLink className="w-3 h-3 transition-transform group-hover/link:translate-x-0.5" />
            </a>
          </div>
        )}
      </div>

      {/* Decorative border gradient */}
      <div 
        className="absolute bottom-0 left-0 right-0 h-1 opacity-0 group-hover:opacity-100 transition-opacity duration-500"
        style={{ 
          background: `linear-gradient(90deg, ${primary}, ${secondary})`
        }}
      />
    </div>
  );
};

// Main Accreditations Component
const Accreditations = ({ accreditations, apiBaseURL = '' }) => {
  const { sectionTitle, sectionSubtitle, items = [] } = accreditations || {};
  const { primary, secondary, fontBody, fontHeading } = useBrandColors();

  if (!items || items.length === 0) return null;

  return (
    <section id="accreditations" className="py-16 md:py-24 bg-white overflow-hidden">
      <style dangerouslySetInnerHTML={{__html: `
        .custom-scrollbar::-webkit-scrollbar {
          width: 4px;
        }
        .custom-scrollbar::-webkit-scrollbar-track {
          background: transparent;
        }
        .custom-scrollbar::-webkit-scrollbar-thumb {
          background: #E5E7EB;
          border-radius: 10px;
        }
        .scroll-container-wrapper {
          overflow-x: auto;
          scroll-behavior: smooth;
          -webkit-overflow-scrolling: touch; /* Crisp kinetic momentum scrolling on iOS devices */
        }
        /* Elegant visible custom scrollbar for the desktop horizontal track wrapper */
        .scroll-container-wrapper::-webkit-scrollbar {
          height: 6px;
        }
        .scroll-container-wrapper::-webkit-scrollbar-track {
          background: #F3F4F6;
          border-radius: 10px;
        }
        .scroll-container-wrapper::-webkit-scrollbar-thumb {
          background: #D1D5DB;
          border-radius: 10px;
        }
        .scroll-container-wrapper::-webkit-scrollbar-thumb:hover {
          background: #9CA3AF;
        }
      `}} />

      <div className="w-full">
        <div className="container mx-auto px-6 lg:px-12 mb-12 md:mb-16">
          <div className="max-w-7xl mx-auto">
            {(sectionTitle || sectionSubtitle) && (
              <div className="text-center">
                <span 
                  className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full text-xs font-medium tracking-[0.15em] uppercase mb-4"
                  style={{ 
                    color: secondary,
                    backgroundColor: `${secondary}15`,
                    fontFamily: fontBody,
                  }}
                >
                  <FiShield className="w-3.5 h-3.5" />
                  Recognitions
                </span>
                
                {sectionTitle && (
                  <h2 
                    className="text-3xl md:text-4xl lg:text-5xl font-bold mb-3"
                    style={{ 
                      color: primary,
                      fontFamily: fontHeading,
                    }}
                  >
                    {sectionTitle}
                  </h2>
                )}
                {sectionSubtitle && (
                  <p 
                    className="text-lg font-light max-w-2xl mx-auto"
                    style={{ 
                      color: '#6B7280',
                      fontFamily: fontBody,
                    }}
                  >
                    {sectionSubtitle}
                  </p>
                )}
                <div className="mt-6 flex justify-center">
                  <div 
                    className="h-1 w-20 rounded-full" 
                    style={{ 
                      background: `linear-gradient(90deg, ${primary}, ${secondary})` 
                    }} 
                  />
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Clean, Non-duplicated Native Horizontal Track */}
        <div className="container mx-auto px-6 lg:px-12">
          <div className="scroll-container-wrapper flex gap-6 pb-6 select-text">
            {items.map((item) => (
              <AccreditationCard 
                key={item._id || item.id || item.name} 
                item={item} 
                apiBaseURL={apiBaseURL}
              />
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default Accreditations;